import { DRINK_TYPES, Collapse, Header } from '@breward/ui';
import DOMPurify from 'dompurify';
import { useTranslation } from 'react-i18next';
import { BeerGuidelines } from './types';
import './ShowStylePage.css';

import { SensoryCard } from './components/SensoryCard';
import { IngredientsCard } from './components/IngredientsCard';
import { ParametersCard } from './components/ParametersCard';
import { ProfileCard } from './components/ProfileCard';
import { SensorySummaryCard } from './components/SensorySummaryCard';

interface StylePageContentProps {
    style: any;
    schemaData?: any;
    /** Shows the back arrow when given. */
    onBack?: () => void;
}

/**
 * The style page as participants and judges see it. Takes the style as data, so it also
 * renders a style that is still being edited (the preview in the style creator).
 */
export const StylePageContent = ({ style, schemaData, onBack }: StylePageContentProps) => {
    const [t] = useTranslation();
    const gl = style?.guidelines as BeerGuidelines | undefined;

    const tabs = [
        { id: 'appearance', label: t('Pages:ParticipantWeb.ShowStylePage.Tabs.appearance', 'Wygląd') },
        { id: 'aroma', label: t('Pages:ParticipantWeb.ShowStylePage.Tabs.aroma', 'Aromat') },
        { id: 'flavor', label: t('Pages:ParticipantWeb.ShowStylePage.Tabs.flavor', 'Smak') },
        { id: 'bitterness', label: t('Pages:ParticipantWeb.ShowStylePage.Tabs.bitterness', 'Goryczka') },
        { id: 'mouthfeel', label: t('Pages:ParticipantWeb.ShowStylePage.Tabs.mouthfeel', 'Odczucie') },
        { id: 'comments', label: t('Pages:ParticipantWeb.ShowStylePage.Tabs.comments', 'Komentarz') },
        { id: 'ingredients', label: t('Pages:ParticipantWeb.ShowStylePage.Tabs.ingredients', 'Surowce') },
        { id: 'general', label: t('Pages:ParticipantWeb.ShowStylePage.Tabs.general', 'Opis stylu') }
    ];

    const scrollToSection = (id: string) => {
        const element = document.getElementById(`section-${id}`);
        if (element) {
            element.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
    };

    return (
        <div className="relative min-h-screen px-4 pb-16 xl:px-0">
            <div className="2xl:max-w-8xl relative z-50 mx-auto max-w-6xl pt-8">
                {onBack && (
                    <div
                        onClick={onBack}
                        className="border-primary-500 dark:border-primary-400 mb-8 flex h-8 w-8 cursor-pointer items-center justify-center rounded-full border-2 border-solid p-2 transition-colors hover:bg-primary-50 dark:hover:bg-primary-900/30 xl:-ml-8"
                    >
                        <div className="icon-[ph--arrow-left] text-primary-500 dark:text-primary-400 h-8 w-8" />
                    </div>
                )}

                {/* Header */}
                <div className="flex items-center gap-4 mb-6 font-sans">
                    {style?.image ? (
                        <div className="h-16 w-16 overflow-hidden rounded-full border border-solid border-amber-500/20 shadow-sm flex-shrink-0">
                            <img src={style.image} alt={style.name} className="h-full w-full object-cover" />
                        </div>
                    ) : (
                        <div className="flex h-16 w-16 items-center justify-center rounded-full bg-amber-500/10 text-amber-600 dark:text-amber-400 border border-solid border-amber-500/20 flex-shrink-0">
                            <div className="icon-[ph--beer-bottle] h-9 w-9" />
                        </div>
                    )}
                    <div>
                        <h1 className="text-3xl font-extrabold text-primary-900 dark:text-primary-100 leading-none mb-1">
                            {style?.name || 'Sample Beer Style'}
                        </h1>
                        <div className="text-xs font-semibold text-primary-500 flex items-center gap-1.5">
                            <span>{style?.drinkType ? (DRINK_TYPES as any)[style.drinkType] || style.drinkType : 'Beer'}</span>
                        </div>
                    </div>
                </div>

                {gl?.summary && (
                    <div className="text-primary-700 dark:text-primary-300 text-base font-medium italic mb-6 leading-relaxed max-w-4xl font-sans">
                        {gl.summary}
                    </div>
                )}

                {/* Sticky Sub-navigation Tabs */}
                <div className="sticky top-0 bg-base-100/90 dark:bg-content-700/95 backdrop-blur-md z-30 -mx-4 px-4 py-3 border-b border-solid border-primary-100 dark:border-primary-900/40 mb-6 flex flex-wrap gap-2 overflow-x-auto scrollbar-none">
                    {tabs.map(tab => (
                        <button
                            key={tab.id}
                            onClick={() => scrollToSection(tab.id)}
                            className="appearance-none border-none outline-none bg-primary-50 dark:bg-primary-950/20 text-primary-700 dark:text-primary-300 hover:bg-primary-100 dark:hover:bg-primary-900/30 cursor-pointer px-4 py-1.5 text-xs font-semibold rounded-full transition-all duration-200"
                        >
                            {tab.label}
                        </button>
                    ))}
                </div>

                <div className="grid gap-6 lg:grid-cols-12">
                    {/* Main Content (Left Column) */}
                    <div className="space-y-6 lg:col-span-8">
                        <SensoryCard
                            id="appearance"
                            title={t('Pages:ParticipantWeb.ShowStylePage.Section.appearance', 'Wygląd')}
                            icon="icon-[ph--eye-bold]"
                            highlight={gl?.appearanceHighlight}
                            bodyText={gl?.appearanceText}
                            guidelinesSectionKey="appearance"
                            gl={gl}
                            schemaData={schemaData}
                        />

                        <SensoryCard
                            id="aroma"
                            title={t('Pages:ParticipantWeb.ShowStylePage.Section.aroma', 'Aromat')}
                            icon="icon-[ph--drop-bold]"
                            highlight={gl?.aromaHighlight}
                            bodyText={gl?.aromaText}
                            guidelinesSectionKey="aroma"
                            gl={gl}
                            schemaData={schemaData}
                        />

                        <SensoryCard
                            id="flavor"
                            title={t('Pages:ParticipantWeb.ShowStylePage.Section.flavor', 'Smak')}
                            icon="icon-[ph--beer-stein]"
                            highlight={gl?.flavorHighlight}
                            bodyText={gl?.flavorText}
                            guidelinesSectionKey="taste"
                            gl={gl}
                            schemaData={schemaData}
                        />

                        <SensoryCard
                            id="bitterness"
                            title={t('Pages:ParticipantWeb.ShowStylePage.Section.bitterness', 'Goryczka')}
                            icon="icon-[ph--shield-bold]"
                            highlight={gl?.bitternessHighlight}
                            bodyText={gl?.bitternessText}
                            guidelinesSectionKey="bitterness"
                            gl={gl}
                            schemaData={schemaData}
                        />

                        <SensoryCard
                            id="mouthfeel"
                            title={t('Pages:ParticipantWeb.ShowStylePage.Section.mouthfeel', 'Odczucie w ustach')}
                            icon="icon-[ph--hand-bold]"
                            highlight={gl?.mouthfeelHighlight}
                            bodyText={gl?.mouthfeelText}
                            guidelinesSectionKey="mouthfeel"
                            gl={gl}
                            schemaData={schemaData}
                        />

                        <SensoryCard
                            id="comments"
                            title={t('Pages:ParticipantWeb.ShowStylePage.Section.comments', 'Komentarz')}
                            icon="icon-[ph--chat-circle-dots-bold]"
                            highlight={gl?.commentsHighlight}
                            bodyText={gl?.commentsText}
                        />

                        <IngredientsCard gl={gl} />

                        {/* Collapsed Full Style Description at the bottom */}
                        <div id="section-general" className="scroll-mt-20">
                            <Collapse
                                title={t('Pages:ParticipantWeb.ShowStylePage.Section.general', 'Pełny opis stylu / wrażenie ogólne')}
                                variant="plain"
                                density="comfortable"
                            >
                                {style?.description ? (
                                    <div
                                        className="style-description text-primary-800 dark:text-primary-100 text-sm leading-relaxed font-sans"
                                        dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(style.description) }}
                                    />
                                ) : (
                                    <p className="text-sm italic text-primary-500 font-sans">
                                        {t('Pages:ParticipantWeb.ShowStylePage.Section.noDescription', 'Brak pełnego opisu stylu')}
                                    </p>
                                )}
                            </Collapse>
                        </div>
                    </div>

                    {/* Sidebar (Right Column) */}
                    <div className="space-y-6 lg:col-span-4">
                        {style?.image && (
                            <div className="overflow-hidden rounded-2xl border border-solid border-white/20 dark:border-primary-800/70 bg-white/20 dark:bg-gradient-to-b dark:from-content-900 dark:to-primary-950/70 backdrop-blur-xl shadow-sm">
                                <div className="relative h-48 w-full overflow-hidden bg-primary-50/50 dark:bg-content-800/50">
                                    <img
                                        src={style.image}
                                        alt={style.name}
                                        className="h-full w-full object-cover transition-transform duration-300 hover:scale-105"
                                    />
                                </div>
                            </div>
                        )}

                        <ParametersCard gl={gl} />

                        <ProfileCard gl={gl} />

                        <SensorySummaryCard gl={gl} />

                        {/* Tip */}
                        <div className="rounded-xl border border-solid border-amber-500/20 bg-amber-500/5 p-4 flex gap-3 text-amber-800 dark:text-amber-400 font-sans text-xs">
                            <div className="icon-[ph--lightbulb-bold] h-5 w-5 flex-shrink-0 text-amber-500" />
                            <div>
                                <span className="font-bold block mb-1">
                                    {t('Pages:ParticipantWeb.ShowStylePage.Tip.title', 'Wskazówka')}
                                </span>
                                <span>
                                    {t('Pages:ParticipantWeb.ShowStylePage.Tip.content', 'Skorzystaj z zakładek u góry, aby szybko przejść do interesującej sekcji stylu.')}
                                </span>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default StylePageContent;
